import { RoomEvent } from 'matrix-js-sdk/src';
import { CallMembership } from 'matrix-js-sdk/src/matrixrtc/CallMembership';
import { MatrixRTCSession, MatrixRTCSessionEvent } from 'matrix-js-sdk/src/matrixrtc/MatrixRTCSession';
import { createContext, PropsWithChildren, useEffect, useState } from 'react';

import useRoomType, { RoomType } from 'components/BreakoutRooms/useRoomType';
import useRoomIdentityContext from 'contexts/RoomIdentityContext/useRoomIdentityContext';
import logger from 'utils/logging/faro';
import { SetState } from 'utils/types';

interface RoomStateContextType {
  mxRtcSession?: MatrixRTCSession;
  setRtcSession: SetState<MatrixRTCSession | undefined>;
  roomType: RoomType;
  activeRoomId: string;
  setActiveRoomId: SetState<string>;
  chatRoomId: string | undefined;
  setChatRoomId: SetState<string | undefined>;
}

export const RoomStateContext = createContext<RoomStateContextType | null>(null);

export const encryptionLogPrefix = '[Call Encryption]';

const RoomStateContextProvider = ({ children }: PropsWithChildren) => {
  const [mxRtcSession, setRtcSession] = useState<MatrixRTCSession>();
  const {
    state: { roomId },
  } = useRoomIdentityContext();
  const [activeRoomId, setActiveRoomId] = useState(roomId);
  const [chatRoomId, setChatRoomId] = useState<string>();

  const roomType = useRoomType(activeRoomId);

  useEffect(() => {
    if (!mxRtcSession) return;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onEncryptionKeyChanged = (_: any, __: any, participantId: string): void =>
      logger.info(`${encryptionLogPrefix} Key changed event triggered - participant id: ${participantId}`);
    mxRtcSession.addListener(MatrixRTCSessionEvent.EncryptionKeyChanged, onEncryptionKeyChanged);

    const onJoinStateChange = (isJoined: boolean): void =>
      logger.info(`${encryptionLogPrefix} My own join state changed - isJoined: ${isJoined}`);
    mxRtcSession.addListener(MatrixRTCSessionEvent.JoinStateChanged, onJoinStateChange);

    // NOTE: MembershipManagerError is handled by useCallEncryptionFallback (#8172), which owns the
    // gated reload recovery. Keeping a duplicate info-only listener here would double-handle the signal.

    const onMembershipsChanged = (oldMemberships: CallMembership[], newMemberships: CallMembership[]) =>
      logger.info(
        `${encryptionLogPrefix} Memberships changed event - old memberships: ${JSON.stringify(
          oldMemberships
        )}, new memberships: ${JSON.stringify(newMemberships)}`
      );
    mxRtcSession.addListener(MatrixRTCSessionEvent.MembershipsChanged, onMembershipsChanged);

    mxRtcSession.room.on(RoomEvent.Timeline, (event) =>
      logger.info(
        `${encryptionLogPrefix} Room timeline event decryption status- isBeingDecrypted: ${event.isBeingDecrypted()}, isDecryptionFailure: ${event.isDecryptionFailure()}`
      )
    );

    return () => {
      mxRtcSession.removeListener(MatrixRTCSessionEvent.EncryptionKeyChanged, onEncryptionKeyChanged);
      mxRtcSession.removeListener(MatrixRTCSessionEvent.JoinStateChanged, onJoinStateChange);
      mxRtcSession.removeListener(MatrixRTCSessionEvent.MembershipsChanged, onMembershipsChanged);
      logger.info('Clearing RTC session on RoomStateContextProvider unmount');
      setRtcSession(undefined);
    };
  }, [mxRtcSession]);

  return (
    <RoomStateContext.Provider
      value={{
        mxRtcSession,
        setRtcSession,
        roomType,
        activeRoomId,
        setActiveRoomId,
        chatRoomId,
        setChatRoomId,
      }}
    >
      {children}
    </RoomStateContext.Provider>
  );
};

export default RoomStateContextProvider;
